
import React, { useState, useContext, memo, useCallback, useEffect, useMemo } from 'react';
import { PageContext } from '../PageContext';
import { Page, UserProfile, GroupGame, Puzzle } from '../types';
import { isUserOnline } from '../utils';
import { useAdmin } from '../AdminContext';
import { useAuth } from '../AuthContext';
import { usePuzzles } from '../PuzzleContext';

const UserResultItem: React.FC<{
    user: UserProfile;
    onAddFriend: (uid: string) => void;
    onFollow: (uid: string) => void;
    isFriend: boolean;
    isFollowing: boolean;
    requestSent: boolean;
    onUserClick: (user: UserProfile) => void;
}> = memo(({ user, onAddFriend, onFollow, isFriend, isFollowing, requestSent, onUserClick }) => (
    <div onClick={() => onUserClick(user)} className="flex items-center p-3 space-x-3 bg-white rounded-lg shadow-sm cursor-pointer hover:bg-gray-50">
        <div className="relative flex-shrink-0">
            <img src={user.photoURL} alt={user.displayName} className="w-12 h-12 rounded-full object-cover" />
            {isUserOnline(user) && (
                <span className="absolute bottom-0 right-0 block h-3.5 w-3.5 rounded-full bg-green-500 border-2 border-white" title="Online"></span>
            )}
        </div>
        <div className="flex-1 min-w-0">
            <p className="font-bold truncate">{user.displayName}</p>
            {user.username && <p className="text-xs font-semibold text-purple-500">@{user.username}</p>}
            <p className="text-sm text-gray-500 truncate">{user.bio}</p>
        </div>
        <div className="flex-shrink-0 flex items-center space-x-2">
            {isFriend ? (
                <button
                    onClick={(e) => { 
                        e.stopPropagation(); 
                        if (window.confirm(`Are you sure you want to unfriend ${user.displayName}?`)) {
                            onFollow(user.uid);
                        }
                    }}
                    className="bg-red-100 text-red-600 border border-red-200 font-bold text-xs px-3 py-1 rounded-md hover:bg-red-200 transition-colors"
                >
                    Unfriend
                </button>
            ) : isFollowing ? (
                 <button
                    onClick={(e) => { 
                        e.stopPropagation(); 
                        if (window.confirm("Cancel friend request?")) {
                            onFollow(user.uid);
                        }
                    }}
                    className="bg-gray-200 text-gray-600 font-bold text-xs px-3 py-1 rounded-md hover:bg-gray-300"
                >
                    Request Sent
                </button>
            ) : (
                <button
                    onClick={(e) => { e.stopPropagation(); onAddFriend(user.uid); }}
                    disabled={requestSent}
                    className="bg-[var(--accent-pink)] text-white font-bold text-xs px-3 py-1 rounded-md disabled:bg-gray-400 hover:bg-pink-600"
                >
                    {requestSent ? 'Sending...' : 'Add Friend'}
                </button>
            )}
        </div>
    </div>
));


const GroupResultItem: React.FC<{ group: GroupGame, onGroupClick: (group: GroupGame) => void }> = memo(({ group, onGroupClick }) => (
    <div onClick={() => onGroupClick(group)} className="flex items-center p-3 space-x-3 bg-white rounded-lg shadow-sm cursor-pointer hover:bg-gray-50">
        <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
            <i className="ph-duotone ph-users-three text-3xl text-purple-600"></i>
        </div>
        <div className="flex-1 min-w-0">
            <p className="font-bold truncate">{group.name}</p>
            <p className="text-sm text-gray-500">{group.members.length} members</p>
        </div>
    </div>
));

const PuzzleResultItem: React.FC<{ puzzle: Puzzle, onPuzzleClick: (puzzle: Puzzle) => void }> = memo(({ puzzle, onPuzzleClick }) => (
    <div onClick={() => onPuzzleClick(puzzle)} className="flex items-center p-3 space-x-3 bg-white rounded-lg shadow-sm cursor-pointer hover:bg-gray-50">
        <img src={puzzle.imageUrl} alt={puzzle.clue} className="w-12 h-12 rounded-lg object-cover" />
        <div className="flex-1 min-w-0">
            <p className="font-bold truncate">{puzzle.clue}</p>
            <p className="text-sm text-gray-500">by {puzzle.creatorProfile.displayName} in "{puzzle.category}"</p>
        </div>
    </div>
));

const searchQuotes = [
    "Find a friend, start a rivalry.",
    "Looking for a specific puzzle?",
    "Discover new groups to join.",
    "Who are you searching for today?",
    "Search the cosmos for a new challenge."
];

const SearchPage: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState<'friends' | 'groups' | 'puzzles'>('friends');
    const pageContext = useContext(PageContext);
    const { sendFriendRequest, sentRequests } = useAdmin();
    const { userProfile, toggleFollow, allUsers, groups } = useAuth();
    const { puzzles } = usePuzzles();
    const [quoteIndex, setQuoteIndex] = useState(0);
    const [quoteOpacity, setQuoteOpacity] = useState(1);
    
    // Initialize recent searches from localStorage
    const [recentSearches, setRecentSearches] = useState<(UserProfile | GroupGame | Puzzle)[]>(() => {
        try {
            const saved = localStorage.getItem('recentSearches');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });

    const clearRecentSearches = () => {
        setRecentSearches([]);
        localStorage.removeItem('recentSearches');
    };

    // Helper to add items to recent history
    const addToRecent = useCallback((item: UserProfile | GroupGame | Puzzle) => {
        setRecentSearches(prev => {
            const itemId = 'uid' in item ? item.uid : item.id;
            // Filter out duplicates based on ID
            const filtered = prev.filter(i => {
                const iId = 'uid' in i ? i.uid : i.id;
                return iId !== itemId;
            });
            // Add new item to top and limit to 5
            const newRecent = [item, ...filtered].slice(0, 5);
            localStorage.setItem('recentSearches', JSON.stringify(newRecent));
            return newRecent;
        });
    }, []);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setQuoteOpacity(0);
            setTimeout(() => {
                setQuoteIndex(prevIndex => (prevIndex + 1) % searchQuotes.length);
                setQuoteOpacity(1);
            }, 500);
        }, 5000);
        return () => clearInterval(intervalId);
    }, []);


    const filteredUsers = useMemo(() => {
        if (!searchTerm || !userProfile) return [];
        const term = searchTerm.toLowerCase();
        return allUsers.filter(user => {
            const iHaveBlockedThem = userProfile.blockedUsers?.includes(user.uid);
            const theyHaveBlockedMe = user.blockedUsers?.includes(userProfile.uid);
            
            const matchName = user.displayName.toLowerCase().includes(term);
            const matchUsername = user.username?.toLowerCase().includes(term);
            const matchBio = (user.bio || '').toLowerCase().includes(term);

            return !iHaveBlockedThem && !theyHaveBlockedMe &&
                   (matchName || matchUsername || matchBio) &&
                   user.uid !== userProfile.uid;
        });
    }, [searchTerm, allUsers, userProfile]);

    const filteredGroups = useMemo(() => {
        if (!searchTerm) return [];
        return groups.filter(group =>
            group.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, groups]);
        
    const filteredPuzzles = useMemo(() => {
        if (!searchTerm || !userProfile) return [];
        return puzzles.filter(puzzle => {
            const creator = allUsers.find(u => u.uid === puzzle.creatorId);
            if (!creator) return false;
            const iHaveBlockedThem = userProfile.blockedUsers?.includes(creator.uid);
            const theyHaveBlockedMe = creator.blockedUsers?.includes(userProfile.uid);

            return !iHaveBlockedThem && !theyHaveBlockedMe &&
                   (puzzle.clue.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   puzzle.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   puzzle.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   puzzle.creatorProfile.displayName.toLowerCase().includes(searchTerm.toLowerCase()));
        });
    }, [searchTerm, puzzles, userProfile, allUsers]);

    const itemsWithAds = useCallback((items: any[], prefix: string) => {
        return items;
    }, []);
        
    const handleUserClick = useCallback((user: UserProfile) => {
        addToRecent(user);
        pageContext?.switchPage(Page.Profile, user.displayName, { userId: user.uid });
    }, [pageContext, addToRecent]);
    
    const handleGroupClick = useCallback((group: GroupGame) => {
        addToRecent(group);
        pageContext?.switchPage(Page.GameDetail, group.name, { gameId: group.id });
    }, [pageContext, addToRecent]);
    
    const handlePuzzleClick = useCallback((puzzle: Puzzle) => {
        addToRecent(puzzle);
        pageContext?.switchPage(Page.PostDetail, 'Post', { puzzle });
    }, [pageContext, addToRecent]);
    
    if (!userProfile) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="spinner-white" style={{ borderColor: 'rgba(107, 33, 168, 0.3)', borderLeftColor: 'var(--primary-dark-purple)'}}></div>
            </div>
        );
    }

    const activeTabClasses = "bg-white/10 backdrop-blur-sm";
    const inactiveTabClasses = "bg-transparent text-white/70";

    return (
        <div className="space-y-4">
            <div className="bg-[var(--primary-dark-purple)] p-3 rounded-lg shadow-md">
                <div className="flex items-center space-x-3">
                    <div className="relative flex-grow bg-white rounded-lg px-4 py-3 text-center flex items-center justify-center min-h-[56px]">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="relative z-10 w-full bg-transparent border-0 focus:ring-0 focus:outline-none text-center text-3xl font-bold aurora-text"
                            style={{ fontFamily: "'Caveat', cursive" }}
                            aria-label="Search for friends, groups, or puzzles"
                        />
                        {!searchTerm && (
                            <p
                                className="absolute inset-0 z-0 flex items-center justify-center text-3xl font-bold aurora-text pointer-events-none"
                                style={{
                                    fontFamily: "'Caveat', cursive",
                                    transition: 'opacity 0.5s ease-in-out',
                                    opacity: quoteOpacity,
                                }}
                                aria-hidden="true"
                            >
                                {searchQuotes[quoteIndex]}
                            </p>
                        )}
                    </div>
                    <i className="ph-duotone ph-magnifying-glass text-2xl text-[var(--accent-pink)]"></i>
                </div>
            </div>

            {searchTerm && (
                 <div className="p-1 rounded-full flex space-x-1 bg-gradient-to-r from-purple-800 to-pink-800">
                    <button onClick={() => setActiveTab('friends')} className={`flex-1 py-2 font-bold rounded-full transition-all duration-300 ${activeTab === 'friends' ? activeTabClasses : inactiveTabClasses}`}>
                         <span className={activeTab === 'friends' ? 'aurora-text' : ''}>People</span>
                    </button>
                    <button onClick={() => setActiveTab('groups')} className={`flex-1 py-2 font-bold rounded-full transition-all duration-300 ${activeTab === 'groups' ? activeTabClasses : inactiveTabClasses}`}>
                         <span className={activeTab === 'groups' ? 'aurora-text' : ''}>Groups</span>
                    </button>
                    <button onClick={() => setActiveTab('puzzles')} className={`flex-1 py-2 font-bold rounded-full transition-all duration-300 ${activeTab === 'puzzles' ? activeTabClasses : inactiveTabClasses}`}>
                         <span className={activeTab === 'puzzles' ? 'aurora-text' : ''}>Puzzles</span>
                    </button>
                </div>
            )}
            
            <div className="space-y-2">
                {!searchTerm && (
                    <>
                        <div className="flex justify-between items-center px-1">
                            <h3 className="text-lg font-bold"><span className="aurora-text">Recent Searches</span></h3>
                            {recentSearches.length > 0 && (
                                <button onClick={clearRecentSearches} className="text-sm font-bold aurora-text hover:opacity-80 transition-opacity">
                                    Clear
                                </button>
                            )}
                        </div>
                        {recentSearches.length > 0 ? (
                             recentSearches.map(item => {
                                 if ('displayName' in item) { // UserProfile
                                    const isMutualFriend = userProfile.following.includes(item.uid) && (allUsers.find(u=>u.uid === item.uid)?.following.includes(userProfile.uid) ?? false);
                                    return <UserResultItem
                                        key={`user-${item.uid}`}
                                        user={item}
                                        onAddFriend={sendFriendRequest}
                                        onFollow={toggleFollow}
                                        isFriend={isMutualFriend}
                                        isFollowing={userProfile.following.includes(item.uid) || false}
                                        requestSent={sentRequests.has(item.uid)}
                                        onUserClick={handleUserClick}
                                      />
                                 } else if ('clue' in item) { // Puzzle
                                    return <PuzzleResultItem key={`puzzle-${item.id}`} puzzle={item} onPuzzleClick={handlePuzzleClick} />;
                                 } else if ('name' in item) { // GroupGame
                                    return <GroupResultItem key={`group-${item.id}`} group={item} onGroupClick={handleGroupClick} />;
                                 }
                                 return null;
                            })
                        ) : (
                            <p className="text-center text-gray-500 py-4">No recent searches.</p>
                        )}
                    </>
                )}

                {searchTerm && activeTab === 'friends' && (
                    filteredUsers.length > 0 ? (
                        itemsWithAds(filteredUsers, 'people').map(item => {
                            const isMutualFriend = userProfile.following.includes(item.uid) && item.following.includes(userProfile.uid);
                            return (
                                <UserResultItem
                                    key={item.uid}
                                    user={item}
                                    onAddFriend={sendFriendRequest}
                                    onFollow={toggleFollow}
                                    isFriend={isMutualFriend}
                                    isFollowing={userProfile.following.includes(item.uid) || false}
                                    requestSent={sentRequests.has(item.uid)}
                                    onUserClick={handleUserClick}
                                />
                            );
                        })
                    ) : <p className="text-center text-gray-500 pt-8">No people found.</p>
                )}
                 {searchTerm && activeTab === 'groups' && (
                    filteredGroups.length > 0 ? (
                        itemsWithAds(filteredGroups, 'groups').map(item => {
                            return <GroupResultItem key={item.id} group={item} onGroupClick={handleGroupClick} />;
                        })
                    ) : <p className="text-center text-gray-500 pt-8">No groups found.</p>
                )}
                 {searchTerm && activeTab === 'puzzles' && (
                    filteredPuzzles.length > 0 ? (
                        itemsWithAds(filteredPuzzles, 'puzzles').map(item => {
                            return <PuzzleResultItem key={item.id} puzzle={item} onPuzzleClick={handlePuzzleClick} />;
                        })
                    ) : <p className="text-center text-gray-500 pt-8">No puzzles found.</p>
                )}
            </div>
        </div>
    );
};

export default SearchPage;
